/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import { oneOf, string } from 'prop-types';
import { FormattedMessage, FormattedDate, intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes, DATE_TYPE_DATE } from '../../util/types';
import { dateFromAPIToLocalNoon } from '../../util/dates';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { txIsCanceled, txIsDelivered, txIsDeclined } from '../../util/customTransaction/mainRentalTransaction';
import config from '../../config';
import moment from 'moment';

import css from './TransactionPanel.css';

const { Money } = sdkTypes;
const CURRENCY = config.currency;

const BookingPeriod = props => {
  const { startDate, endDate, dateType } = props;

  const timeFormatOptions =
    dateType === DATE_TYPE_DATE
      ? {
        weekday: 'long',
      }
      : {
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
      };

  const dateFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  return (
    <>
      <div className={css.bookingPeriod}>
        <div className={css.bookingPeriodSection}>
          <div className={css.dayLabel}>
            <FormattedMessage id="BookingBreakdown.bookingStart" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={startDate} {...timeFormatOptions} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={startDate} {...dateFormatOptions} />
          </div>
        </div>

        <div className={css.bookingPeriodSectionRigth}>
          <div className={css.dayLabel}>
            <FormattedMessage id="BookingBreakdown.bookingEnd" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={endDate} {...timeFormatOptions} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={endDate} {...dateFormatOptions} />
          </div>
        </div>
      </div>
    </>
  );
};

export const BookingBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    intl,
    dateType,
  } = props;

  const isProvider = userRole === 'provider';

  const { extendBookingData } = transaction.attributes.protectedData || {};
  const { startDate, endDate, extendPrice } = extendBookingData || {};
  const displayStart = moment(startDate).toDate();
  const displayEnd = moment(endDate).subtract(13, 'hours').toDate();

  const priceTotal = new Money(extendPrice.amount, CURRENCY);
  const formattedTotal = formatMoney(intl, priceTotal);

  let providerTotalMessageId = 'BookingBreakdown.providerTotalDefault';
  if (txIsDelivered(transaction)) {
    providerTotalMessageId = 'BookingBreakdown.providerTotalDelivered';
  } else if (txIsDeclined(transaction)) {
    providerTotalMessageId = 'BookingBreakdown.providerTotalDeclined';
  } else if (txIsCanceled(transaction)) {
    providerTotalMessageId = 'BookingBreakdown.providerTotalCanceled';
  }

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : (
    <FormattedMessage id="BookingBreakdown.total" />
  );

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>

      <div className={css.lineItem}>
        <BookingPeriod startDate={displayStart} endDate={displayEnd} dateType={dateType} />
      </div>
      <hr className={css.totalDivider} />

      {!extendPrice ? null : (
        <>
          <div className={css.lineItem}>
            <span className={css.itemLabel}>
              <FormattedMessage id="LineItemExtendBookingMaybe.extendBooking" />
            </span>
            <span className={css.itemValue}>{formattedTotal}</span>
          </div>
          <hr className={css.totalDivider} />
        </>
      )}

      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotal}</div>
      </div>

    </div>
  );
};

BookingBreakdownComponent.defaultProps = { rootClassName: null, className: null, dateType: null };

BookingBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  dateType: propTypes.dateType,

  // from injectIntl
  intl: intlShape.isRequired,
};

const BookingBreakdown = injectIntl(BookingBreakdownComponent);

BookingBreakdown.displayName = 'BookingBreakdown';

export default BookingBreakdown;
